import { ChangeEvent, InputHTMLAttributes, ReactNode } from "react";
import { Control, ControllerRenderProps } from "react-hook-form";
import { FormControl, FormField, FormItem, FormMessage } from "./form";
import { cn } from "@drpcrd/utils/shared";

type Props = {
  control: Control<any>;
  name: string;
  label: ReactNode;
  type: "datetime-local" | "date";
  defaultTo6pm?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

export const InputDatePicker = ({
  control,
  name,
  label,
  type,
  defaultTo6pm = false,
}: Props) => {
  const handleDateChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<any, string>,
  ) => {
    if (type === "date") {
      field.onChange(new Date(e.target.value).toISOString());
      return;
    }

    const rawValue = e.target.value;
    if (!rawValue) {
      field.onChange(null);
      return;
    }

    const [datePart, timePart] = rawValue.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);

    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));

    field.onChange(utcDate);
  };

  const handleValue = (field: ControllerRenderProps<any, string>): string => {
    if (type === "datetime-local") {
      if (field.value) {
        return new Date(field.value).toISOString().slice(0, 16);
      } else if (defaultTo6pm) {
        const now = new Date();
        now.setHours(18, 0, 0, 0);
        return now.toISOString().slice(0, 16);
      } else {
        return "";
      }
    } else {
      return field.value
        ? new Date(field.value).toISOString().split("T")[0]
        : "";
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className="mt-4 w-full">
          <FormControl>
            <label className="flex w-full flex-row items-center gap-3">
              <div
                className={cn(
                  "w-20 text-sm md:text-lg",
                  error?.message && "text-pink-500",
                )}
              >
                {label}
                <FormMessage className="text-pink-500" />
              </div>

              <input
                value={handleValue(field)}
                onChange={(e) => handleDateChange(e, field)}
                type={type}
                className="grow rounded-lg border bg-white px-2 py-[10px] font-sans text-lg font-normal lg:w-4/12 lg:grow-0"
              />
            </label>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
